import { TitleCasePipe } from '@angular/common';
import { STATUSCONSTANTS } from '@app/shared/constants/constants';
import { ColDef } from 'ag-grid-community';

// Common ellipsis cell renderer for text truncation
const createEllipsisCellRenderer = (
  width: string = '180px',
  maxWidth: string = '200px',
  tooltipField?: string,
) => {
  return (params: any) => {
    const value = params.value || '';
    return `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width:${width}; max-width:${maxWidth};">${value}</div>`;
  };
};

export const ID_FIELD: ColDef = {
  field: 'id',
  headerName: 'ID',
  width: 100,
  sortable: true,
  filter: false,
};

export const NAME_FIELD: ColDef = {
  field: 'name',
  headerName: 'Name',
  sortable: true,
  filter: false,
  flex: 1,
};

export const STATUS_FIELD: ColDef = {
  field: 'status',
  headerName: 'Status',
  sortable: true,
  filter: false,
  width: 120,
};

export const LEAVE_COMMENTS_FIELD: ColDef = {
  field: 'comments',
  headerName: 'Comments',
  sortable: true,
  filter: false,
  width: 120,
  tooltipField: 'comments',
};

export const TIMESHEET_STATUS: ColDef = {
  field: 'status',
  headerName: 'Status',
  sortable: true,
  filter: false,
  width: 120,
  minWidth: 80  ,
};

export const MONTH: ColDef = {
  field: 'month',
  headerName: 'Month',
  sortable: true,
  filter: false,
  minWidth: 160,
};

export const WORKING_HRS: ColDef = {
  field: 'workingHrs',
  headerName: 'Total Working Hrs',
  sortable: true,
  filter: false,
  minWidth: 140,
  width: 140,
};

export const BILLABLE_HRS: ColDef = {
  field: 'billableHrs',
  headerName: 'Billable Hrs',
  sortable: true,
  filter: false,
  minWidth: 140,
  width: 140,
};

export const LEAVES_AVAILED: ColDef = {
  field: 'leavesAvailed',
  headerName: 'Leaves Availed',
  sortable: true,
  filter: false,
  minWidth: 140,
  width: 140,
};

export const CALENDAR_VIEW_ACTION: ColDef = {
  field: 'calendarView',
  headerName: 'Action',
  minWidth: 100,
  width: 120,
  maxWidth: 150,
  sortable: false,
  filter: false,
  cellRenderer: (params: any) => {
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.height = '100%';
    wrapper.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const parent = params?.context?.componentParent;
      if (parent && typeof parent.openCalendar === 'function') {
        parent.openCalendar(e, params.data);
      }
    });

    const btn = document.createElement('button');
    btn.title = 'View calendar';
    btn.style.background = 'transparent';
    btn.style.border = 'none';
    btn.style.cursor = 'pointer';
    btn.style.display = 'flex';
    btn.style.alignItems = 'center';
    btn.style.justifyContent = 'center';
    btn.style.padding = '4px';
    btn.type = 'button';

    const icon = document.createElement('i');
    icon.className = 'material-icons';
    icon.textContent = 'date_range';
    icon.style.fontSize = '20px';
    icon.style.color = '#1A1A1A';

    btn.appendChild(icon);
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const parent = params?.context?.componentParent;
      if (parent && typeof parent.openCalendar === 'function') {
        parent.openCalendar(e, params.data);
      }
    });

      const submitBtn = document.createElement('button');
      submitBtn.title = 'Submit';
      const status = params?.data?.status?.toLowerCase() || '';
      const workingHours = params?.data?.timesheetMonthSummary?.workingHours ?? 0;
      const billableHours = params?.data?.timesheetMonthSummary?.billableHours ?? 0;
      const isDisabled = status === 'approved' || status === 'pending approval' || (workingHours === 0 && billableHours === 0);
      submitBtn.disabled = isDisabled;
      submitBtn.style.background = isDisabled ? '#cccccc' : '#003f2d';
      submitBtn.style.color = '#ffffff';
      submitBtn.style.border = 'none';
      submitBtn.style.borderRadius = '4px';
      submitBtn.style.cursor = isDisabled ? 'not-allowed' : 'pointer';
      submitBtn.style.display = 'flex';
      submitBtn.style.alignItems = 'center';
      submitBtn.style.justifyContent = 'center';
      submitBtn.style.padding = '6px';
      submitBtn.style.marginLeft = '8px';
      submitBtn.style.fontSize = '12px';
      submitBtn.style.opacity = isDisabled ? '0.6' : '1';
      submitBtn.type = 'button';
      submitBtn.textContent = 'Submit';

      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!submitBtn.disabled) {
          const parent = params?.context?.componentParent;
          if (parent && typeof parent.onSubmitTimesheet === 'function') {
            parent.onSubmitTimesheet(params.data);
          }
        }
      });

      wrapper.appendChild(btn);
      wrapper.appendChild(submitBtn);
      return wrapper;
    },
  }

export const ACTIONS_FIELD: ColDef = {
  field: 'actions',
  headerName: 'Actions',
  minWidth: 100,
  maxWidth: 300,
  sortable: false,
  filter: false,
  pinned: 'right',
  autoHeight: true,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
};
export const RESOURCE_FIELD: ColDef = {
  field: 'firstName', // Primary field for sorting, but renderer uses firstName, lastName, employeeId, designation
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 200,
  maxWidth: 400,
  cellRenderer: 'ResourceNameCellRenderer',
  tooltipField: 'firstName',
  autoHeight: true,
  cellStyle: { padding: '8px' },
  suppressSizeToFit: true,
  pinned: 'left',
  // Custom value getter to combine fields for sorting/filtering
  valueGetter: (params: any) => {
    const firstName = params.data?.firstName || '';
    const lastName = params.data?.lastName || '';
    const name = params.data?.fullName || '';
    const resourceName = params.data?.resourceName || '';

    // Return the display name for sorting purposes
    return firstName && lastName
      ? `${firstName} ${lastName}`
      : name || resourceName?.split('\n')[0] || '';
  },
};

export const EMAIL_ID_FIELD: ColDef = {
  field: 'email',
  headerName: 'Email ID',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'email',
};

export const RM_FIELD: ColDef = {
  field: 'reportingManager',
  headerName: 'RM',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 200,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'reportingManager',
};

export const LEADER_FIELD: ColDef = {
  field: 'dntLeader',
  headerName: 'Leader',
  sortable: true,
  filter: false,
  minWidth: 150,
  width: 150,
  maxWidth: 200,
  cellRenderer: createEllipsisCellRenderer('130px', '180px'),
  tooltipField: 'dntLeader',
};

export const COMPANY_FIELD: ColDef = {
  field: 'employer',
  headerName: 'Company',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 200,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'employer',
};

export const POI_TYPE_FIELD: ColDef = {
  field: 'poiType',
  headerName: 'POI Type',
  sortable: true,
  filter: false,
  width: 400,
};

export const LAPTOP_FIELD: ColDef = {
  field: 'laptopType',
  headerName: 'Laptop',
  sortable: true,
  filter: false,
  width: 180,
};

export const ACCESSORIES_FIELD: ColDef = {
  field: 'accessoriesType',
  headerName: 'Accessories',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 400,
  cellRenderer: createEllipsisCellRenderer('180px', '380px'),
  tooltipField: 'accessoriesType',
};

export const DOJ_FIELD: ColDef = {
  field: 'lastHireDate',
  headerName: 'DOJ',
  sortable: true,
  filter: false,
};

export const LAPTOP_ISSUE_DATE_FIELD: ColDef = {
  field: 'dateOfPickup',
  headerName: 'Laptop Issue Date',
  sortable: true,
  filter: false,
  width: 200,
  maxWidth: 200,
  minWidth: 200,
  valueFormatter: (params: any) => {
    const v = params.value;
    if (!v) return '-';
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toISOString().substring(0, 10);
    } catch {
      return v;
    }
  },
};

export const LAPTOP_SERIAL_FIELD: ColDef = {
  field: 'laptopSerialNumber',
  headerName: 'Laptop S/N',
  width: 150,
  maxWidth: 150,
  sortable: true,
  filter: false,
  cellRenderer: 'CopyCellRenderer',
  autoHeight: false,
  suppressSizeToFit: true,
};

export const CHARGER_SERIAL_FIELD: ColDef = {
  field: 'chargerSerialNumber',
  headerName: 'Charger S/N',
  width: 150,
  maxWidth: 150,
  sortable: true,
  filter: false,
  cellRenderer: 'CopyCellRenderer',
  autoHeight: false,
  suppressSizeToFit: true,
};

export const STATUS_FIELD_REQUEST: ColDef = {
  field: 'status',
  headerName: 'Status',
  sortable: true,
  filter: false,
  minWidth: 200,
  maxWidth: 200,
  cellRenderer: 'statusRenderer',
  cellRendererParams: {
    showDropdown: false,
    category: 'Request',
  },
};

export const REQUEST_STATUS_FIELD: ColDef = {
  field: 'status',
  headerName: 'Status',
  sortable: true,
  filter: false,
  minWidth: 200,
  maxWidth: 200,
  cellRenderer: 'statusRenderer',
  cellRendererParams: {
    showDropdown: false,
    category: 'Request',
  },
};

export const REQUESTED_BY_OFFBOARDING_FIELD: ColDef = {
  field: 'createdBy',
  headerName: 'Requested By',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 400,
  cellRenderer: createEllipsisCellRenderer('180px', '380px'),
  tooltipField: 'createdBy',
};

export const DATE_OF_OFFBOARDING_FIELD: ColDef = {
  field: 'dateOfOffBoarding',
  headerName: 'LWD',
  width: 150,
  // minWidth: 210,
  sortable: true,
  filter: false,
};

export const STATUS_OFFBOARDING_FIELD: ColDef = {
  field: 'returnStatus',
  headerName: 'Status',
  flex: 1.5,
  sortable: true,
  filter: false,
  cellRenderer: (params: any) => {
    const value = params.value || STATUSCONSTANTS.INITIATED;
    const options = [
      {
        value: STATUSCONSTANTS.PENDING,
        label: 'Pending',
        colorClass: 'status-pending',
      },
      {
        value: STATUSCONSTANTS.DONE,
        label: 'Done',
        colorClass: 'status-cost-received',
      },
      {
        value: STATUSCONSTANTS.CANCELLED,
        label: 'Cancelled',
      },
      {
        value: STATUSCONSTANTS.INITIATED,
        label: 'Initiated',
      },
    ];
    const selected = options.find((opt) => opt.value === value) || options[0];
    return `
    <div class="status-label-wrapper">
      <select class="status-dropdown" onchange="window.onReturnStatusChange && window.onReturnStatusChange('${params.data.employeeId}', this.value)">
        ${options.map((opt) => `<option value="${opt.value}" ${opt.value === value ? 'selected' : ''}> ${opt.label}</option>`).join('')}
      </select>
    </div>
  `;
  },
};

export const EMPLOYEE_STATUS_FIELD: ColDef = {
  field: 'status',
  headerName: 'Employee Status',
  width: 130,
  minWidth: 200,
  sortable: true,
  filter: false,
  cellRenderer: (params: any) => {
    const status = params.value;
    if (!status) return '';
    const statusLower = status.toLowerCase();
    let iconHtml = '';
    if (statusLower === 'active') {
      iconHtml = `<emerald-icon icon="person" class="title-icon" color="emerald" type="outlined"></emerald-icon>`;
    } else if (statusLower === 'inactive') {
      iconHtml = `<emerald-icon icon="person-off" class="title-icon" color="error" type="outlined"></emerald-icon>`;
    }
    return `<span class="employee-status status-${statusLower}">${iconHtml}${status}</span>`;
  },
};

export const DATE_OF_RETURN_FIELD: ColDef = {
  field: 'dateOfReturn',
  headerName: 'Date of Return',
  width: 180,
  minWidth: 180,
  sortable: true,
  filter: false,
  cellRenderer: 'DateOfReturnCell',
  cellStyle: {
    padding: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  editable: false,
};

export const LAST_WORKING_DAY_FIELD: ColDef = {
  field: 'dateOfOffBoarding',
  headerName: 'LWD',
  width: 150,
  sortable: true,
  filter: false,
};

export const CHECKBOX_FIELD: ColDef = {
  field: '',
  headerName: '',
  width: 50,
  minWidth: 50,
  maxWidth: 50,
  pinned: 'left',
  lockPosition: 'left',
  suppressMovable: true,
  sortable: false,
  filter: false,
  resizable: false,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
};

export const EMP_ID_FIELD: ColDef = {
  field: 'empId',
  headerName: 'Employee ID',
  width: 120,
  sortable: true,
  filter: false,
};

export const LOCATION: ColDef = {
  field: 'location',
  headerName: 'Location',
  minWidth: 200,
  width: 120,
  sortable: true,
  filter: false,
};

export const COMMENTS_FIELD: ColDef = {
  field: 'comments',
  headerName: 'Comments',
  width: 300,
  sortable: true,
  filter: false,
  cellRenderer: (params: any) => {
    const raw = params.value;
    const hasComments = !!raw && raw.toString().trim().length > 0;
    const label = hasComments ? 'View/Add Comments' : 'Add Comments';
    const buttonStyle = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: 150px;
      height: 32px;
      background-color: rgba(0, 63, 45, 0.04);
      color: #003f2d;
      border: 1px solid rgba(0, 63, 45, 0.1);
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.2s;
      margin-top: 12px;
    `;
    // Keep same onclick hook; assumes global handler wired elsewhere.
    return `<button class="btn-link" style="${buttonStyle}" onclick="openComments('${params.data.id}')">${label}</button>`;
  },
};

export const VENDOR_NAME: ColDef = {
  field: 'vendorName',
  headerName: 'Vendor Name',
  sortable: true,
  filter: false,
  minWidth: 400,
  width: 400,
  maxWidth: 400,
  cellRenderer: (params: any) => {
    const vendorId = params.data.vendorId || 0;
    const vendorName = params.value;

    return `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width:380px; max-width:380px;">
    <a href="/ops-vendor-management/vendors-profile/${vendorId}/${vendorName}" style="color: #003f2d; text-decoration: underline;"
       onclick="window.angularNavigate('/ops-vendor-management/vendors-profile/${vendorId}/${vendorName}')">${new TitleCasePipe().transform(vendorName)}</a></div>`;
  },
  tooltipField: 'vendorName',
};

export const Total_CWR: ColDef = {
  field: 'totalCount',
  headerName: 'Total No. of CWR',
  sortable: true,
  filter: false,
  resizable: true,
  flex: 1,
  minWidth: 300,
  maxWidth: 500,
};

export const Onboarded_CWR: ColDef = {
  field: 'onboardedCount',
  headerName: 'Onboarded CWR',
  sortable: true,
  filter: false,
  resizable: true,
  flex: 1,
  minWidth: 300,
  maxWidth: 500,
};

export const Offboarded_CWR: ColDef = {
  field: 'offboardedCount',
  headerName: 'Offboarded CWR',
  sortable: true,
  filter: false,
  flex: 2,
  minWidth: 300,
  maxWidth: 500,
  resizable: true,
};

// Inventory colDefs

export const PRODUCT_NAME: ColDef = {
  field: 'product',
  headerName: 'Product Name',
  tooltipField: 'itemName',
  minWidth: 400,
  maxWidth: 400,
  width: 400,
  pinned: 'left',
  sortable: true,
  filter: false,
  cellRenderer: (params: any) => {
    const name = params.value || '';
    const rowDataJson = JSON.stringify(params.data || {});
    const escapedJson = rowDataJson
      .replace(/'/g, "\\'")
      .replace(/"/g, '&quot;');

    return `
      <button class="btn-link product-link"
        style="text-align:left; cursor:pointer; background:none; border:none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width:360px; max-width:360px;"
        onclick='openInventoryProductView(${rowDataJson})'>
        ${name}
      </button>
    `;
  },
};

export const PRODUCT_NAME_ORDER: ColDef = {
  field: 'itemName',
  headerName: 'Product Name',
  tooltipField: 'itemName',
  minWidth: 400,
  width: 400,
  maxWidth: 400,
  pinned: 'left',
  sortable: true,
  filter: false,
  cellClass: 'ellipsis-text-inventory',
  cellRenderer: (params: any) => {
    const name = params.value || '';
    return `<div style="display:flex; align-items:center; margin-top:10px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; width:360px; max-width:360px;">${name}</div>`;
  },
};

export const TOTAL_QUANTITY: ColDef = {
  field: 'availableQuantity',
  headerName: 'Available Quantity',
  headerTooltip: 'Total available quantity',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const ISSUED_QUANTITY: ColDef = {
  field: 'issuedQuantity',
  headerName: 'Issued Quantity',
  minWidth: 200,
  sortable: true,
  filter: false,
  flex: 1,
};

export const PROCURED_QUANTITY: ColDef = {
  field: 'procuredQuantity',
  headerName: 'Procured Quantity',
  headerTooltip: 'Total procured quantity',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const DATE_OF_ISSUE: ColDef = {
  field: 'dateOfIssue',
  headerName: 'Date of Issue',
  minWidth: 200,
  sortable: true,
  filter: false,
  flex: 1,
};

export const DATE_OF_PROCUREMENT: ColDef = {
  field: 'dateOfProcurement',
  headerName: 'Date of Procurement',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const DELIVERY_STATUS: ColDef = {
  field: 'deliveryStatus',
  headerName: 'Delivery Status',
  minWidth: 240,
  width: 300,
  sortable: true,
  filter: false,
  flex: 1,
  cellRenderer: 'statusRenderer',
  cellRendererParams: {
    showDropdown: true,
    category: 'Inventory',
  },
};

// Procured (Orders tab) colDefs — bound to the procurement-summary API response
export const CATEGORY_FIELD: ColDef = {
  field: 'category',
  headerName: 'Category',
  minWidth: 200,
  sortable: true,
  filter: false,
  pinned: 'left',
};

export const PRODUCT_NAME_PROCURED: ColDef = {
  field: 'item',
  headerName: 'Product Name',
  tooltipField: 'item',
  minWidth: 250,
  sortable: true,
  filter: false,
};

export const OPENING_QUANTITY: ColDef = {
  field: 'openingQuantity',
  headerName: 'Opening Quantity',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const DATE_OF_PURCHASE: ColDef = {
  field: 'dateOfPurchase',
  headerName: 'Date of Purchase',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const QUANTITY_PURCHASED: ColDef = {
  field: 'quantityPurchased',
  headerName: 'Quantity Purchased',
  minWidth: 200,
  sortable: true,
  filter: false,
};

export const AVAILABLE_BALANCE: ColDef = {
  field: 'availableBalance',
  headerName: 'Available Balance',
  minWidth: 200,
  sortable: true,
  filter: false,
  flex: 1,
};

// My Request table field definitions
export const FULL_NAME_FIELD: ColDef = {
  field: 'fullName',
  headerName: 'Full Name',
  sortable: true,
  filter: false,
  width: 200,
  pinned: 'left',
};

export const EMPLOYEE_ID_FIELD: ColDef = {
  field: 'employeeId',
  headerName: 'Employee ID',
  sortable: true,
  filter: false,
  width: 120,
};

export const LAST_HIRE_DATE_FIELD: ColDef = {
  field: 'lastHireDate',
  headerName: 'DOJ',
  sortable: true,
  filter: false,
  width: 160,
  minWidth: 160,
  maxWidth: 160,
};

export const ACCESSORIES_TYPE_FIELD: ColDef = {
  field: 'accessoriesType',
  headerName: 'Accessories Type',
  sortable: true,
  filter: false,
  width: 200,
  minWidth: 200,
  maxWidth: 400,
  cellRenderer: createEllipsisCellRenderer('180px', '380px'),
  tooltipField: 'accessoriesType',
};

export const CREATED_BY_FIELD: ColDef = {
  field: 'createdBy',
  headerName: 'Requested By',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 200,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'createdBy',
};

export const EMPLOYER_FIELD: ColDef = {
  field: 'employer',
  headerName: 'Company',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  maxWidth: 200,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'employer',
};

export const PICKUP_DATE_FIELD: ColDef = {
  field: 'pickUpDate',
  headerName: 'Pickup Date',
  sortable: true,
  filter: false,
  width: 130,
};

export const OS_TYPE: ColDef = {
  field: 'osType',
  headerName: 'Assets',
  width: 150,
  sortable: true,
  filter: false,
};

export const DAMAGE_COST: ColDef = {
  field: 'damageCost',
  headerName: 'Damage Cost',
  width: 150,
  sortable: true,
  filter: false,
  valueFormatter: (params: any) => (params.value ? `₹${params.value}` : ''),
};
export const RESOURCE_OFFBOARDING_FIELD: ColDef = {
  field: 'firstName',
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  autoHeight: true,
  cellStyle: { padding: '8px 24px' },
  suppressSizeToFit: true,
  pinned: 'left',
  cellRenderer: (params: any) => {
    const d = params.data || {};
    const firstName = d.firstName || '';
    const lastName = d.lastName || '';
    const designation = d.designation || d.jobTitle || '';
    const employeeIdentifier = d.employeeId || d.empId || '';
    const numericId = typeof d.id === 'number' ? d.id : null;
    const displayName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || d.name || d.fullName || '';
    const target = numericId !== null ? numericId : employeeIdentifier;
    return `
      <div class="emp-cell">
        <a class="emp-name" onclick="event.preventDefault(); if(window.openResourceProfileOffboarding){window.openResourceProfileOffboarding('${target}');} return false;">${new TitleCasePipe().transform(displayName)}</a>
        <div class="emp-meta" title="${employeeIdentifier} | ${designation}">${employeeIdentifier} | ${designation}</div>
      </div>
    `;
  },
};

export const RESOURCE_PLAIN_FIELD: ColDef = {
  field: 'firstName',
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  autoHeight: true,
  cellStyle: { padding: '8px 24px' },
  suppressSizeToFit: true,
  pinned: 'left',
  cellRenderer: (params: any) => {
    const d = params.data || {};
    const firstName = d.firstName || '';
    const lastName = d.lastName || '';
    const designation = d.designation || d.jobTitle || '';
    const employeeIdentifier = d.employeeId || d.empId || '';
    const displayName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || d.name || d.fullName || '';
    return `
      <div class="emp-cell">
        <span class="emp-name" style="font-weight:400;font-size:14px;line-height:1.1;text-align:left;text-decoration:none;cursor:default;margin:0;padding:0;" title="${displayName}">${new TitleCasePipe().transform(displayName)}</span>
        <div class="emp-meta" style="font-size:12px;color:#6c757d;line-height:1.1;margin:0;padding:0;">${employeeIdentifier} | ${designation}</div>
      </div>
    `;
  },
};

export const RETURN_STATUS_OFFBOARDING_FIELD: ColDef = {
  field: 'returnStatus',
  headerName: 'Status',
  minWidth: 240,
  width: 300,
  sortable: true,
  filter: false,
  cellRenderer: 'statusRenderer',
  cellRendererParams: {
    category: 'Return',
    showDropdown: true,
  },
};

export const EMPLOYEE_PLAIN_FIELD: ColDef = {
  field: 'firstName',
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  autoHeight: true,
  cellStyle: { padding: '8px 24px' },
  suppressSizeToFit: true,
  pinned: 'left',
  cellRenderer: (params: any) => {
    const d = params.data || {};
    const firstName = d.firstName || '';
    const lastName = d.lastName || '';
    const designation = d.designation || d.jobTitle || '';
    const employeeIdentifier = d.employeeId || d.empId || '';
    const displayName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || d.name || d.fullName || '';
    return `
      <div class="emp-cell">
        <span class="emp-name" style="font-weight:400;font-size:14px;line-height:1.1;text-align:left;text-decoration:none;cursor:default;margin:0;padding:0;" title="${displayName}">${new TitleCasePipe().transform(displayName)}</span>
        <div class="emp-meta" style="font-size:12px;color:#6c757d;line-height:1.1;margin:0;padding:0;">${employeeIdentifier} | ${designation}</div>
      </div>
    `;
  },
};

export const RESOURCE_ASSETALLOCATED_FIELD: ColDef = {
  field: 'firstName',
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  autoHeight: true,
  cellStyle: { padding: '8px 24px' },
  suppressSizeToFit: true,
  pinned: 'left',
  cellRenderer: (params: any) => {
    const d = params.data || {};
    const firstName = d.firstName || '';
    const lastName = d.lastName || '';
    const designation = d.designation || d.jobTitle || '';
    const employeeIdentifier = d.employeeId || d.empId || '';
    const numericId = typeof d.id === 'number' ? d.id : null;
    const displayName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || d.name || d.fullName || '';
    const target = numericId !== null ? numericId : employeeIdentifier;
    return `
      <div class="emp-cell">
        <a class="emp-name" onclick="event.preventDefault(); if(window.openAssetResourceProfile){window.openAssetResourceProfile('${target}');} return false;" title="${displayName}">${new TitleCasePipe().transform(displayName)}</a>
        <div class="emp-meta" title="${employeeIdentifier} | ${designation}">${employeeIdentifier} | ${designation}</div>
      </div>
    `;
  },
};

export const OFFBOARDING_COMMENTS_FIELD: ColDef = {
  field: 'comments',
  headerName: 'Comments',
  width: 200,
  minWidth: 250,
  sortable: true,
  filter: false,
  cellRenderer: 'addCommentsRenderer',
};
export const RESOURCE_DAMAGE_FIELD: ColDef = {
  field: 'firstName',
  headerName: 'Resource',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  autoHeight: true,
  cellStyle: { padding: '8px 24px' },
  suppressSizeToFit: true,
  pinned: 'left',
  cellRenderer: (params: any) => {
    const d = params.data || {};
    const firstName = d.firstName || '';
    const lastName = d.lastName || '';
    const designation = d.designation || d.jobTitle || '';
    const employeeIdentifier = d.employeeId || d.empId || '';
    const numericId = typeof d.id === 'number' ? d.id : null;
    const status = d.damageCostStatus || '';
    const isCancelled = status === 'cancelled' || status === 'Cancelled';
    const displayName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || d.name || d.fullName || '';
    const target = numericId !== null ? numericId : employeeIdentifier;

    if (isCancelled) {
      return `
        <div class="emp-cell">
          <span class="emp-name" style="color: #6c757d; cursor: default; text-decoration: none;" title="${displayName}">${new TitleCasePipe().transform(displayName)}</span>
          <div class="emp-meta" title="${employeeIdentifier} | ${designation}">${employeeIdentifier} | ${designation}</div>
        </div>
      `;
    }

    return `
      <div class="emp-cell">
        <a class="emp-name" onclick="event.preventDefault(); if(window.openDamageResourceProfile){window.openDamageResourceProfile('${target}');} return false;" title="${displayName}">${new TitleCasePipe().transform(displayName)}</a>
        <div class="emp-meta" title="${employeeIdentifier} | ${designation}">${employeeIdentifier} | ${designation}</div>
      </div>
    `;
  },
};
export const DATE_OF_ENTRY: ColDef = {
  field: 'createdDate',
  headerName: 'Date of Entry',
  sortable: true,
  filter: false,
  minWidth: 200,
  width: 200,
  valueFormatter: (params) => {
    if (!params.value) return '';
    const date = new Date(params.value);
    return date.toISOString().split('T')[0];
  },
};

export const NO_OF_LEAVES: ColDef = {
  field: 'noOfLeaves',
  headerName: 'No. of Leaves',
  sortable: true,
  filter: false,
  minWidth: 220,
  maxWidth: 420,
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'noOfLeaves',
};

export const DURATION: ColDef = {
  field: 'leaveStartDate',
  headerName: 'Duration',
  sortable: true,
  filter: false,
  minWidth: 220,
  width: 220,
  maxWidth: 420,
  valueGetter: (params: any) => {
    const start = params.data?.leaveStartDate;
    const end = params.data?.leaveEndDate;
    if (!start && !end) return '';
    if (start === end || !end) return start || '';
    return `${start} - ${end}`;
  },
  cellRenderer: createEllipsisCellRenderer('180px', '200px'),
  tooltipField: 'leaveStartDate',
};

export const ATTACHMENT: ColDef = {
  field: 'fileAttachmentUrl',
  headerName: 'Attachment',
  sortable: true,
  filter: false,
  minWidth: 220,
  width: 220,
  maxWidth: 420,
  cellRenderer: 'attachmentRenderer',
  tooltipField: 'attachment',
};